import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useOperators } from 'dashboard/components-next/filter/operators';
import { UTM_FIELDS } from './commercialFormatters';

// Campos personalizados de Negociação habilitados em Configurações > Campos
// ("Habilitar campo para filtro") entram no painel com este prefixo na chave.
const CUSTOM_PREFIX = 'custom:';

// Campos do painel de filtros do Dashboard Comercial: Pipeline, Vendedor, cada
// campo de UTM e cada campo personalizado habilitado aceitam vários valores
// (utmValues: campo → valores já usados nas negociações). Só "Igual a" por ora.
export function useCommercialFilterTypes(utmValues = ref({})) {
  const { t } = useI18n();
  const pipelinesStore = usePipelinesStore();
  const agents = useMapGetter('agents/getAgents');
  const attributesByModel = useMapGetter('attributes/getAttributesByModel');
  const { operators } = useOperators();

  const filterOperators = computed(() => [operators.value.equal_to]);

  const toOptions = list => list.map(({ id, name }) => ({ id, name }));

  const buildType = (attributeKey, label, extra) => ({
    attributeKey,
    value: attributeKey,
    attributeName: label,
    label,
    dataType: 'text',
    filterOperators: filterOperators.value,
    attributeModel: 'standard',
    ...extra,
  });

  const toValueOptions = values =>
    values.map(value => ({ id: value, name: value }));

  // Lista, múltipla escolha e checkbox têm opções próprias; os demais tipos
  // usam os valores já preenchidos nas negociações (como os de UTM).
  const customOptions = attribute => {
    if (attribute.attribute_display_type === 'checkbox') {
      return {
        inputType: 'multiSelect',
        options: [
          { id: 'true', name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
          { id: 'false', name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
        ],
      };
    }
    if (
      ['list', 'multiple_choice'].includes(attribute.attribute_display_type)
    ) {
      return {
        inputType: 'multiSelect',
        options: toValueOptions(attribute.attribute_values || []),
      };
    }
    return {
      inputType: 'customMultiSelect',
      options: toValueOptions(utmValues.value[attribute.attribute_key] || []),
    };
  };

  const customTypes = computed(() =>
    attributesByModel
      .value('deal_attribute')
      .filter(
        attribute =>
          attribute.filterable && !UTM_FIELDS.includes(attribute.attribute_key)
      )
      .map(attribute =>
        buildType(
          `${CUSTOM_PREFIX}${attribute.attribute_key}`,
          attribute.attribute_display_name,
          customOptions(attribute)
        )
      )
  );

  const filterTypes = computed(() => [
    buildType('pipeline_id', t('DASHBOARD.COMERCIAL.FILTER.PIPELINE'), {
      inputType: 'multiSelect',
      options: toOptions(pipelinesStore.getPipelinesList),
    }),
    buildType('user_id', t('DASHBOARD.COMERCIAL.FILTER.SELLER'), {
      inputType: 'multiSelect',
      options: toOptions(agents.value),
    }),
    ...UTM_FIELDS.map(field =>
      buildType(
        field,
        t(`DASHBOARD.COMERCIAL.UTM_FILTER.FIELDS.${field.toUpperCase()}`),
        {
          inputType: 'customMultiSelect',
          options: (utmValues.value[field] || []).map(value => ({
            id: value,
            name: value,
          })),
        }
      )
    ),
    ...customTypes.value,
  ]);

  return { filterTypes };
}

// Linhas aplicadas do painel → parâmetros da API (cada campo vira uma lista).
// Campos personalizados vão agrupados em `custom_filters` (chave → valores).
export const filtersToParams = filters => {
  const params = {};
  filters.forEach(({ attributeKey, values }) => {
    const ids = values.map(item => item.id);
    if (attributeKey.startsWith(CUSTOM_PREFIX)) {
      params.custom_filters = {
        ...params.custom_filters,
        [attributeKey.slice(CUSTOM_PREFIX.length)]: ids,
      };
    } else {
      params[attributeKey] = ids;
    }
  });
  return params;
};
